import React, { useEffect, useRef, useState } from 'react';
import FlexComponent from '../FlexComponent';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Avatar } from 'primereact/avatar';
import './form.css';
import { ErrorMessage } from 'formik';

function WorkHistoryComponent({ values, isSecondJob, errors, touched, handleBlur, setFieldValue }) {
  // Initialize error state as an object with entries for each work history index and new entry
  const [error, setError] = useState({ newEntry: {}, workHistory: [] });
  const [currentWork, setCurrentWork] = useState({
    title: '',
    yearly_income: '',
    years_in_industry: '',
    company_name: '',
    employment_type: 'Salary - W2 Employee',
    status: '',
    start_date: null,
    end_date: null,
    full_address: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    industry_type: '',
    monthly_overtime: '',
    monthly_bonuses: '',
    monthly_commissions: '',
    is_second_job: isSecondJob,
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentGapExplanation, setCurrentGapExplanation] = useState(values.work_history_gap_explanation || '');
  const [addMoreWorkHistory, setAddMoreWorkHistory] = useState(false);

  // Calculate total work history duration in months
  const calculateTotalWorkDuration = () => {
    if (!values.work_history || values.work_history.length === 0) return 0;
    let totalMonths = 0;
    values.work_history.forEach((work) => {
      if (work.start_date) {
        const start = new Date(work.start_date);
        const end = work.status === 'true' || !work.end_date ? new Date() : new Date(work.end_date);
        const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        totalMonths += months > 0 ? months : 0;
      }
    });
    return totalMonths;
  };

  // Format duration in years and months
  const formatDuration = (months) => {
    if (!months) return '';
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    let result = '';
    if (years > 0) result += `${years} year${years > 1 ? 's' : ''}`;
    if (remainingMonths > 0) result += `${result ? ' ' : ''}${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
    return result || '0 months';
  };

  // Detect gaps in work history
  const detectWorkHistoryGaps = () => {
    if (!values.work_history || values.work_history.length < 2) return [];
    const sortedHistory = [...values.work_history].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    const gaps = [];
    for (let i = 0; i < sortedHistory.length - 1; i++) {
      const currentEnd = sortedHistory[i].status === 'true' || !sortedHistory[i].end_date ? new Date() : new Date(sortedHistory[i].end_date);
      const nextStart = sortedHistory[i + 1].start_date ? new Date(sortedHistory[i + 1].start_date) : null;
      if (currentEnd && nextStart && currentEnd < nextStart) {
        const gapMonths = (nextStart.getFullYear() - currentEnd.getFullYear()) * 12 + (nextStart.getMonth() - currentEnd.getMonth());
        if (gapMonths > 1) {
          gaps.push({
            from: currentEnd,
            to: nextStart,
            months: gapMonths,
          });
        }
      }
    }
    return gaps;
  };

  const handleAddClick = () => {
    const validationErrors = validateWorkEntry(currentWork);
    if (Object.keys(validationErrors).length === 0) {
      const updatedWorkHistory = [...values.work_history, { ...currentWork }];
      setFieldValue('work_history', updatedWorkHistory);
      if (currentGapExplanation) {
        setFieldValue('work_history_gap_explanation', currentGapExplanation);
      }
      setCurrentWork({
        title: '',
        yearly_income: '',
        years_in_industry: '',
        company_name: '',
        employment_type: 'Salary - W2 Employee',
        status: '',
        start_date: null,
        end_date: null,
        full_address: '',
        street_address: '',
        city: '',
        state: '',
        zip_code: '',
        industry_type: '',
        monthly_overtime: '',
        monthly_bonuses: '',
        monthly_commissions: '',
        is_second_job: isSecondJob,
      });
      setCurrentGapExplanation('');
      setError((prev) => ({ ...prev, newEntry: {} }));
      setAddMoreWorkHistory(false);
    } else {
      setError((prev) => ({ ...prev, newEntry: validationErrors }));
    }
  };

  const handleEditClick = (index) => {
    setEditingIndex(index);
    // Initialize errors for this index if not already present
    setError((prev) => {
      const newWorkHistoryErrors = [...prev.workHistory];
      newWorkHistoryErrors[index] = newWorkHistoryErrors[index] || {};
      return { ...prev, workHistory: newWorkHistoryErrors };
    });
  };

  const handleSaveEdit = (index) => {
    const validationErrors = validateWorkEntry(values.work_history[index]);
    if (Object.keys(validationErrors).length === 0) {
      setEditingIndex(null);
      setError((prev) => {
        const newWorkHistoryErrors = [...prev.workHistory];
        newWorkHistoryErrors[index] = {};
        return { ...prev, workHistory: newWorkHistoryErrors };
      });
    } else {
      setError((prev) => {
        const newWorkHistoryErrors = [...prev.workHistory];
        newWorkHistoryErrors[index] = validationErrors;
        return { ...prev, workHistory: newWorkHistoryErrors };
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setError((prev) => {
      const newWorkHistoryErrors = [...prev.workHistory];
      newWorkHistoryErrors[editingIndex] = {};
      return { ...prev, workHistory: newWorkHistoryErrors };
    });
  };

  const handleDeleteClick = (index) => {
    const updatedWorkHistory = values.work_history.filter((_, idx) => idx !== index);
    setFieldValue('work_history', updatedWorkHistory);
    setError((prev) => {
      const newWorkHistoryErrors = [...prev.workHistory];
      newWorkHistoryErrors.splice(index, 1); // Remove errors for deleted entry
      return { ...prev, workHistory: newWorkHistoryErrors };
    });
    if (values.work_history.length === 1) {
      setFieldValue('work_history_gap_explanation', '');
      setCurrentGapExplanation('');
    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedWorkHistory = [...values.work_history];
    updatedWorkHistory[index] = { ...updatedWorkHistory[index], [field]: value };
    setFieldValue('work_history', updatedWorkHistory);
    // Re-validate the entry on change
    const validationErrors = validateWorkEntry(updatedWorkHistory[index]);
    setError((prev) => {
      const newWorkHistoryErrors = [...prev.workHistory];
      newWorkHistoryErrors[index] = validationErrors;
      return { ...prev, workHistory: newWorkHistoryErrors };
    });
  };

  const handleNewEntryChange = (field, value) => {
    setCurrentWork((prev) => ({ ...prev, [field]: value }));
    // Re-validate the new entry on change
    const validationErrors = validateWorkEntry({ ...currentWork, [field]: value });
    setError((prev) => ({ ...prev, newEntry: validationErrors }));
  };

  function changeAvatarColor(type) {
    if (!type) return '';
    if (type.includes('W2')) return '#F141D0';
    if (type === '1099 Independent Contractor') return '#418BCC';
    return '#EB4243';
  }

  function avatarTitle(type) {
    if (!type) return '';
    if (type.includes('W2')) return 'W2';
    if (type === '1099 Independent Contractor') return '1099';
    if (type === 'Self Employed/Business Owner') return 'SE';
    return '';
  }

  const validateWorkEntry = (workEntry) => {
    const errors = {};
    if (!workEntry.title?.trim()) errors.title = true;
    if (!workEntry.yearly_income) errors.yearly_income = true;
    if (!workEntry.years_in_industry) errors.years_in_industry = true;
    if (!workEntry.status) errors.status = true;
    if (workEntry.status === 'false' && !workEntry.start_date) {
      errors.start_date = true;
    }
    if (workEntry.status === 'false' && !workEntry.end_date) {
      errors.end_date = true;
    }
    if (workEntry.status === 'true' && !workEntry.start_date) {
      errors.start_date = true;
    }
    return errors;
  };

  const autoCompleteRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (window.google && window.google.maps) {
      if (!inputRef.current || !(inputRef.current instanceof HTMLInputElement)) return;
      autoCompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['geocode'],
        componentRestrictions: { country: 'us' },
      });

      autoCompleteRef.current.addListener('place_changed', () => {
        const place = autoCompleteRef.current.getPlace();
        let streetNumber = '';
        let route = '';

        setCurrentWork((prev) => ({
          ...prev,
          full_address: place.formatted_address || '',
          street_address: '',
          city: '',
          state: '',
          zip_code: '',
        }));

        const addressComponents = place.address_components || [];
        addressComponents.forEach((component) => {
          const types = component.types;
          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (types.includes('route')) {
            route = component.long_name;
          }
          if (types.includes('locality')) {
            setCurrentWork((prev) => ({ ...prev, city: component.long_name }));
          }
          if (types.includes('administrative_area_level_1')) {
            setCurrentWork((prev) => ({ ...prev, state: component.long_name }));
          }
          if (types.includes('postal_code')) {
            setCurrentWork((prev) => ({ ...prev, zip_code: component.long_name }));
          }
        });

        if (streetNumber || route) {
          setCurrentWork((prev) => ({
            ...prev,
            street_address: `${streetNumber} ${route}`.trim(),
          }));
        }
      });
    }
  }, []);

  const totalWorkDuration = calculateTotalWorkDuration();
  const gaps = detectWorkHistoryGaps();
  const showNewEntryForm = totalWorkDuration < 24; // 2 years = 24 months
  const isCurrentStatusTrue = values.work_history?.some((work) => work.status === 'true') && !isSecondJob;

  return (
    <div>
      {!isSecondJob && (
        <div>
          <p style={{ fontSize: '28px', fontWeight: 'bold', padding: '0.75rem', fontFamily: 'Barlow Semi Condensed' }}>
            I can definitely help. Let’s get started. What do you do for work?
          </p>
        </div>
      )}

      {values.work_history.length > 0 && (
        <div>
          {values.work_history.map((work, index) => (
            <div key={index} style={{ width: '100%' }}>
              {work.is_second_job === isSecondJob && (
                <FlexComponent
                  style={{ background: 'white', margin: '1rem 0', padding: '1rem', borderRadius: '10px' }}
                  gap="1rem"
                  flexDirection="column"
                >
                  {editingIndex !== index ? (
                    <>
                      <FlexComponent alignItems="start" justifyContent="start" gap="1rem" style={{ textAlign: 'start' }}>
                        <div>
                          <Avatar
                            style={{ width: '4.5rem', color: 'white', background: changeAvatarColor(work?.employment_type) }}
                            label={avatarTitle(work?.employment_type)}
                            size="xlarge"
                          />
                        </div>
                        <div style={{ width: '50%' }}>
                          <p>{work?.title || 'No Title'}</p>
                          <p>{work?.employment_type || 'No Employment Type'}</p>
                          <p style={{ color: '#8C8C8C' }}>{work?.years_in_industry || 'No Time at Position'}</p>
                          <p>Start Date: {work?.start_date ? new Date(work.start_date).toLocaleDateString() : 'N/A'}</p>
                          {!work.status && work?.end_date && (
                            <p>End Date: {new Date(work.end_date).toLocaleDateString() || 'N/A'}</p>
                          )}
                          <p>
                            <span style={{ fontSize: '22px', fontWeight: 'bold' }}>$ {work?.yearly_income || '0'}</span>
                            <span style={{ marginLeft: '.3rem' }}>Yearly</span>
                          </p>
                        </div>
                        <Button type="button" onClick={() => handleEditClick(index)}>
                          Edit
                        </Button>
                        {values.work_history.length > 1 && (
                          <Button type="button" onClick={() => handleDeleteClick(index)}>
                            Delete
                          </Button>
                        )}
                      </FlexComponent>
                    </>
                  ) : (
                    <FlexComponent flexDirection="column" gap="1rem" alignItems="start" style={{ width: '100%' }}>
                      <FlexComponent justifyContent="start" className="inputFlexContainer" gap="1rem" alignItems="start" style={{ width: '100%' }}>
                        <div className="inputContainerDiv" style={{ width: '45%', border: error.workHistory[index]?.title && '1px solid red', borderRadius: '5px' }}>
                          <InputText
                            value={work.title}
                            onChange={(e) => handleInputChange(index, 'title', e.target.value)}
                            style={{ width: '100%' }}
                            placeholder="Position/Title"
                          />
                        </div>
                        <div className="inputContainerDiv" style={{ width: '30%' }}>
                          <InputText
                            value={work.company_name}
                            onChange={(e) => handleInputChange(index, 'company_name', e.target.value)}
                            style={{ width: '100%' }}
                            placeholder="Employer/Company Name"
                          />
                        </div>
                        <div className="inputContainerDiv" style={{ width: '25%', border: error.workHistory[index]?.status && '1px solid red', borderRadius: '5px' }}>
                          <Dropdown
                            value={work.status}
                            options={
                              values.work_history[0].status === 'true' && index > 0
                                ? [{ label: 'I used to work in this role', value: 'false' }]
                                : [
                                  { label: 'Currently working in this role', value: 'true' },
                                  { label: 'I used to work in this role', value: 'false' },
                                ]
                            }
                            onChange={(e) => handleInputChange(index, 'status', e.value)}
                            style={{ width: '100%' }}
                            placeholder="Status"
                          />
                        </div>
                      </FlexComponent>

                      <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start" style={{ width: '100%' }}>
                        <div className="inputContainerDiv" style={{ width: '40%' }}>
                          <InputText
                            value={work.full_address}
                            onChange={(e) => handleInputChange(index, 'full_address', e.target.value)}
                            style={{ width: '100%' }}
                            placeholder="Enter Address"
                          />
                        </div>
                      </FlexComponent>

                      <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start" style={{ width: '100%' }}>
                        <div className="inputContainerDiv" style={{ width: '20%', border: error.workHistory[index]?.start_date && '1px solid red', borderRadius: '5px' }}>
                          <div className={`p-inputgroup flex-1 login-input-div`}>
                            <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                              <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                            </span>
                            <Calendar
                              value={work.start_date}
                              onChange={(e) => handleInputChange(index, 'start_date', e.value)}
                              style={{ width: '100%', borderLeft: 'none' }}
                              placeholder="Start Date"
                              className="inputWithIcon"
                            />
                          </div>
                        </div>
                        {work.status === 'false' && (
                          <div className="inputContainerDiv" style={{ width: '20%', border: error.workHistory[index]?.end_date && '1px solid red', borderRadius: '5px' }}>
                            <div className={`p-inputgroup flex-1 login-input-div`}>
                              <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                                <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                              </span>
                              <Calendar
                                value={work.end_date}
                                onChange={(e) => handleInputChange(index, 'end_date', e.value)}
                                style={{ width: '100%' }}
                                placeholder="End Date"
                                className="inputWithIcon"
                              />
                            </div>
                          </div>
                        )}
                        <div className="inputContainerDiv" style={{ width: '20%', border: error.workHistory[index]?.yearly_income && '1px solid red', borderRadius: '5px' }}>
                          <div className={`p-inputgroup flex-1 login-input-div`}>
                            <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                              <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                            </span>
                            <InputText
                              type="number"
                              value={work.yearly_income}
                              onChange={(e) => handleInputChange(index, 'yearly_income', e.target.value)}
                              style={{ width: '100%', borderLeft: 'none' }}
                              placeholder="Yearly Income"
                              className="inputWithIcon"
                            />
                          </div>
                        </div>
                        <div className="inputContainerDiv" style={{ width: '20%' }}>
                          <Dropdown
                            value={work.employment_type}
                            options={[
                              { label: 'Salary - W2 Employee', value: 'Salary - W2 Employee' },
                              { label: 'Hourly - W2 Employee', value: 'Hourly - W2 Employee' },
                              { label: '1099 Independent Contractor', value: '1099 Independent Contractor' },
                              { label: 'Self Employed/Business Owner', value: 'Self Employed/Business Owner' },
                              { label: 'Retired', value: 'Retired' },
                            ]}
                            onChange={(e) => handleInputChange(index, 'employment_type', e.value)}
                            style={{ width: '100%' }}
                            placeholder="Income Type"
                          />
                        </div>
                      </FlexComponent>

                      <FlexComponent className="inputFlexContainer" gap="1rem" alignItems="start" justifyContent="start" style={{ width: '100%' }}>
                        <div className="inputContainerDiv" style={{ width: '30%' }}>
                          <Dropdown
                            value={work.industry_type}
                            options={[
                              { label: 'Healthcare - Doctors, dentists, therapists, nurses, hospitals, clinics', value: 'Healthcare - Doctors, dentists, therapists, nurses, hospitals, clinics' },
                              { label: 'Construction & Trades - Contractors, plumbers, electricians, HVAC, general labor', value: 'Construction & Trades - Contractors, plumbers, electricians, HVAC, general labor' },
                              { label: 'Education - Schools, colleges, teachers, tutors', value: 'Education - Schools, colleges, teachers, tutors' },
                              { label: 'Retail & E-commerce - Stores, boutiques, online sellers', value: 'Retail & E-commerce - Stores, boutiques, online sellers' },
                              { label: 'Food & Hospitality - Restaurants, hotels, bars, caterers', value: 'Food & Hospitality - Restaurants, hotels, bars, caterers' },
                              { label: 'Finance & Insurance - Banks, credit unions, loan officers, advisors, insurance', value: 'Finance & Insurance - Banks, credit unions, loan officers, advisors, insurance' },
                              { label: 'Real Estate - Agents, brokers, appraisers, property managers', value: 'Real Estate - Agents, brokers, appraisers, property managers' },
                              { label: 'Government & Military - Federal, state, local workers, armed forces', value: 'Government & Military - Federal, state, local workers, armed forces' },
                              { label: 'Transportation & Logistics - Truckers, delivery, shipping, warehouses', value: 'Transportation & Logistics - Truckers, delivery, shipping, warehouses' },
                              { label: 'Manufacturing - Factory workers, machine operators, assemblers', value: 'Manufacturing - Factory workers, machine operators, assemblers' },
                              { label: 'Tech & IT - Software, hardware, cybersecurity, support, web services', value: 'Tech & IT - Software, hardware, cybersecurity, support, web services' },
                              { label: 'Legal & Law Enforcement - Lawyers, paralegals, police, security', value: 'Legal & Law Enforcement - Lawyers, paralegals, police, security' },
                              { label: 'Media & Marketing - Advertising, content creators, design, PR', value: 'Media & Marketing - Advertising, content creators, design, PR' },
                              { label: 'Non-Profit & Religious - Churches, charities, community organizations', value: 'Non-Profit & Religious - Churches, charities, community organizations' },
                              { label: 'Energy & Utilities - Oil, gas, electric, water, renewables', value: 'Energy & Utilities - Oil, gas, electric, water, renewables' },
                              { label: 'Arts & Entertainment - Musicians, artists, actors, influencers', value: 'Arts & Entertainment - Musicians, artists, actors, influencers' },
                              { label: 'Professional Services - Consultants, accountants, HR, recruiters', value: 'Professional Services - Consultants, accountants, HR, recruiters' },
                              { label: 'Self-Employed - Freelancers, gig workers, entrepreneurs', value: 'Self-Employed - Freelancers, gig workers, entrepreneurs' },
                              { label: 'Unemployed / Retired - Not currently working or retired', value: 'Unemployed / Retired - Not currently working or retired' },
                              { label: "Other / Unknown - Doesn't fit into a category or unclear", value: "Other / Unknown - Doesn't fit into a category or unclear" },
                            ]}
                            onChange={(e) => handleInputChange(index, 'industry_type', e.value)}
                            style={{ width: '100%' }}
                            placeholder="Industry Type"
                          />
                        </div>
                        <div className="inputContainerDiv" style={{ width: '20%', border: error.workHistory[index]?.years_in_industry && '1px solid red', borderRadius: '5px' }}>
                          <InputText
                            type="number"
                            value={work.years_in_industry}
                            onChange={(e) => handleInputChange(index, 'years_in_industry', e.target.value)}
                            style={{ width: '100%', borderLeft: 'none' }}
                            placeholder="Years In Industry"
                            className="inputWithIcon"
                          />
                        </div>
                      </FlexComponent>

                      <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start" style={{ width: '100%' }}>
                        <div className="inputContainerDiv" style={{ textAlign: 'start', width: '50%' }}>
                          <FlexComponent className="inputFlexContainer">
                            <div style={{ fontSize: '14px', marginRight: '10px' }}>
                              Have you received any bonuses/overtime/commissions?
                            </div>
                            <div className={`p-inputgroup flex-1 login-input-div`}>
                              <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                                <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                              </span>
                              <InputText
                                type="number"
                                value={work.monthly_overtime}
                                onChange={(e) => handleInputChange(index, 'monthly_overtime', e.target.value)}
                                style={{ width: '100%', borderLeft: 'none' }}
                                placeholder="Monthly Overtime"
                                className="inputWithIcon"
                              />
                            </div>
                          </FlexComponent>
                        </div>
                        <div className="inputContainerDiv" style={{ textAlign: 'start', width: '25%' }}>
                          <div className={`p-inputgroup flex-1 login-input-div`}>
                            <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                              <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                            </span>
                            <InputText
                              type="number"
                              value={work.monthly_bonuses}
                              onChange={(e) => handleInputChange(index, 'monthly_bonuses', e.target.value)}
                              style={{ width: '100%', borderLeft: 'none' }}
                              placeholder="Monthly Bonuses"
                              className="inputWithIcon"
                            />
                          </div>
                        </div>
                        <div className="inputContainerDiv" style={{ textAlign: 'start', width: '25%' }}>
                          <div className={`p-inputgroup flex-1 login-input-div`}>
                            <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                              <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                            </span>
                            <InputText
                              type="number"
                              value={work.monthly_commissions}
                              onChange={(e) => handleInputChange(index, 'monthly_commissions', e.target.value)}
                              style={{ width: '100%', borderLeft: 'none' }}
                              placeholder="Monthly Commissions"
                              className="inputWithIcon"
                            />
                          </div>
                        </div>
                      </FlexComponent>

                      <FlexComponent justifyContent="center" gap="1rem">
                        <Button type="button" onClick={() => handleSaveEdit(index)}>
                          Save
                        </Button>
                        <Button type="button" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                      </FlexComponent>
                    </FlexComponent>
                  )}
                </FlexComponent>
              )}
            </div>
          ))}
        </div>
      )}

      {values.work_history.length > 0 && showNewEntryForm && !isSecondJob && (
        <div style={{ fontSize: '20px', fontWeight: 'bold' }}>Great. And, what did you do before that?</div>
      )}

      {(showNewEntryForm || addMoreWorkHistory) && (
        <div style={{ background: 'white', padding: '1rem', borderRadius: '10px' }}>
          <FlexComponent flexDirection="column" gap="1rem" alignItems="start">
            <FlexComponent justifyContent="start" className="inputFlexContainer" gap="1rem" alignItems="start" style={{ width: '100%' }}>
              <div className="inputContainerDiv" style={{ width: '45%', border: error.newEntry.title && '1px solid red', borderRadius: '5px' }}>
                <InputText
                  value={currentWork.title}
                  onChange={(e) => handleNewEntryChange('title', e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="Position/Title"
                />
              </div>
              <div className="inputContainerDiv" style={{ width: '30%' }}>
                <InputText
                  value={currentWork.company_name}
                  onChange={(e) => handleNewEntryChange('company_name', e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="Employer/Company Name"
                />
              </div>
              <div className="inputContainerDiv" style={{ width: '25%', border: error.newEntry.status && '1px solid red', borderRadius: '5px' }}>
                <Dropdown
                  value={currentWork.status}
                  options={
                    isCurrentStatusTrue
                      ? [{ label: 'I used to work in this role', value: 'false' }]
                      : [
                        { label: 'Currently working in this role', value: 'true' },
                        { label: 'I used to work in this role', value: 'false' },
                      ]
                  }
                  onChange={(e) => handleNewEntryChange('status', e.value)}
                  style={{ width: '100%' }}
                  placeholder="Status"
                />
              </div>
            </FlexComponent>

            <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start" style={{ width: '100%' }}>
              <div className="inputContainerDiv" style={{ width: '40%' }}>
                <InputText
                  ref={inputRef}
                  value={currentWork.full_address}
                  onChange={(e) => handleNewEntryChange('full_address', e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="Enter Address"
                />
              </div>
            </FlexComponent>

            <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start" style={{ width: '100%' }}>
              <div className="inputContainerDiv" style={{ width: '20%', border: error.newEntry.start_date && '1px solid red', borderRadius: '5px' }}>
                <div className={`p-inputgroup flex-1 login-input-div`}>
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                  </span>
                  <Calendar
                    value={currentWork.start_date}
                    onChange={(e) => handleNewEntryChange('start_date', e.value)}
                    style={{ width: '100%', borderLeft: 'none' }}
                    placeholder="Start Date"
                    className="inputWithIcon"
                  />
                </div>
              </div>
              {currentWork.status === 'false' && (
                <div className="inputContainerDiv" style={{ width: '20%', border: error.newEntry.end_date && '1px solid red', borderRadius: '5px' }}>
                  <div className={`p-inputgroup flex-1 login-input-div`}>
                    <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                      <i className="pi pi-calendar" style={{ color: 'black', fontWeight: 'bold', padding: '5px' }}></i>
                    </span>
                    <Calendar
                      value={currentWork.end_date}
                      onChange={(e) => handleNewEntryChange('end_date', e.value)}
                      style={{ width: '100%' }}
                      placeholder="End Date"
                      className="inputWithIcon"
                    />
                  </div>
                </div>
              )}
              <div className="inputContainerDiv" style={{ width: '20%', border: error.newEntry.yearly_income && '1px solid red', borderRadius: '5px' }}>
                <div className={`p-inputgroup flex-1 login-input-div`}>
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                  </span>
                  <InputText
                    type="number"
                    value={currentWork.yearly_income}
                    onChange={(e) => handleNewEntryChange('yearly_income', e.target.value)}
                    style={{ width: '100%', borderLeft: 'none' }}
                    placeholder="Yearly Income"
                    className="inputWithIcon"
                  />
                </div>
              </div>
              <div className="inputContainerDiv" style={{ width: '20%' }}>
                <Dropdown
                  value={currentWork.employment_type}
                  options={[
                    { label: 'Salary - W2 Employee', value: 'Salary - W2 Employee' },
                    { label: 'Hourly - W2 Employee', value: 'Hourly - W2 Employee' },
                    { label: '1099 Independent Contractor', value: '1099 Independent Contractor' },
                    { label: 'Self Employed/Business Owner', value: 'Self Employed/Business Owner' },
                    { label: 'Retired', value: 'Retired' },
                  ]}
                  onChange={(e) => handleNewEntryChange('employment_type', e.value)}
                  style={{ width: '100%' }}
                  placeholder="Income Type"
                />
              </div>
            </FlexComponent>

            <FlexComponent className="inputFlexContainer" gap="1rem" alignItems="start" justifyContent="start" style={{ width: '100%' }}>
              <div className="inputContainerDiv" style={{ width: '30%' }}>
                <Dropdown
                  value={currentWork.industry_type}
                  options={[
                    { label: 'Healthcare - Doctors, dentists, therapists, nurses, hospitals, clinics', value: 'Healthcare - Doctors, dentists, therapists, nurses, hospitals, clinics' },
                    { label: 'Construction & Trades - Contractors, plumbers, electricians, HVAC, general labor', value: 'Construction & Trades - Contractors, plumbers, electricians, HVAC, general labor' },
                    { label: 'Education - Schools, colleges, teachers, tutors', value: 'Education - Schools, colleges, teachers, tutors' },
                    { label: 'Retail & E-commerce - Stores, boutiques, online sellers', value: 'Retail & E-commerce - Stores, boutiques, online sellers' },
                    { label: 'Food & Hospitality - Restaurants, hotels, bars, caterers', value: 'Food & Hospitality - Restaurants, hotels, bars, caterers' },
                    { label: 'Finance & Insurance - Banks, credit unions, loan officers, advisors, insurance', value: 'Finance & Insurance - Banks, credit unions, loan officers, advisors, insurance' },
                    { label: 'Real Estate - Agents, brokers, appraisers, property managers', value: 'Real Estate - Agents, brokers, appraisers, property managers' },
                    { label: 'Government & Military - Federal, state, local workers, armed forces', value: 'Government & Military - Federal, state, local workers, armed forces' },
                    { label: 'Transportation & Logistics - Truckers, delivery, shipping, warehouses', value: 'Transportation & Logistics - Truckers, delivery, shipping, warehouses' },
                    { label: 'Manufacturing - Factory workers, machine operators, assemblers', value: 'Manufacturing - Factory workers, machine operators, assemblers' },
                    { label: 'Tech & IT - Software, hardware, cybersecurity, support, web services', value: 'Tech & IT - Software, hardware, cybersecurity, support, web services' },
                    { label: 'Legal & Law Enforcement - Lawyers, paralegals, police, security', value: 'Legal & Law Enforcement - Lawyers, paralegals, police, security' },
                    { label: 'Media & Marketing - Advertising, content creators, design, PR', value: 'Media & Marketing - Advertising, content creators, design, PR' },
                    { label: 'Non-Profit & Religious - Churches, charities, community organizations', value: 'Non-Profit & Religious - Churches, charities, community organizations' },
                    { label: 'Energy & Utilities - Oil, gas, electric, water, renewables', value: 'Energy & Utilities - Oil, gas, electric, water, renewables' },
                    { label: 'Arts & Entertainment - Musicians, artists, actors, influencers', value: 'Arts & Entertainment - Musicians, artists, actors, influencers' },
                    { label: 'Professional Services - Consultants, accountants, HR, recruiters', value: 'Professional Services - Consultants, accountants, HR, recruiters' },
                    { label: 'Self-Employed - Freelancers, gig workers, entrepreneurs', value: 'Self-Employed - Freelancers, gig workers, entrepreneurs' },
                    { label: 'Unemployed / Retired - Not currently working or retired', value: 'Unemployed / Retired - Not currently working or retired' },
                    { label: "Other / Unknown - Doesn't fit into a category or unclear", value: "Other / Unknown - Doesn't fit into a category or unclear" },
                  ]}
                  onChange={(e) => handleNewEntryChange('industry_type', e.value)}
                  style={{ width: '100%' }}
                  placeholder="Industry Type"
                />
              </div>
              <div className="inputContainerDiv" style={{ width: '20%', border: error.newEntry.years_in_industry && '1px solid red', borderRadius: '5px' }}>
                <InputText
                  type="number"
                  value={currentWork.years_in_industry}
                  onChange={(e) => handleNewEntryChange('years_in_industry', e.target.value)}
                  style={{ width: '100%', borderLeft: 'none' }}
                  placeholder="Years In Industry"
                />
              </div>
            </FlexComponent>

            <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start" style={{ width: '100%' }}>
              <div className="inputContainerDiv" style={{ textAlign: 'start', width: '50%' }}>
                <FlexComponent className="inputFlexContainer">
                  <div style={{ fontSize: '14px', marginRight: '10px' }}>
                    Have you received any bonuses/overtime/commissions?
                  </div>
                  <div className={`p-inputgroup flex-1 login-input-div`}>
                    <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                      <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                    </span>
                    <InputText
                      type="number"
                      value={currentWork.monthly_overtime}
                      onChange={(e) => handleNewEntryChange('monthly_overtime', e.target.value)}
                      style={{ width: '100%', borderLeft: 'none' }}
                      placeholder="Monthly Overtime"
                      className="inputWithIcon"
                    />
                  </div>
                </FlexComponent>
              </div>
              <div className="inputContainerDiv" style={{ textAlign: 'start', width: '25%' }}>
                <div className={`p-inputgroup flex-1 login-input-div`}>
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                  </span>
                  <InputText
                    type="number"
                    value={currentWork.monthly_bonuses}
                    onChange={(e) => handleNewEntryChange('monthly_bonuses', e.target.value)}
                    style={{ width: '100%', borderLeft: 'none' }}
                    placeholder="Monthly Bonuses"
                    className="inputWithIcon"
                  />
                </div>
              </div>
              <div className="inputContainerDiv" style={{ textAlign: 'start', width: '25%' }}>
                <div className={`p-inputgroup flex-1 login-input-div`}>
                  <span className="p-inputgroup-addon" style={{ borderRight: 'none' }}>
                    <i className="pi pi-dollar" style={{ background: '#333333', color: 'white', padding: '5px', borderRadius: '30px' }}></i>
                  </span>
                  <InputText
                    type="number"
                    value={currentWork.monthly_commissions}
                    onChange={(e) => handleNewEntryChange('monthly_commissions', e.target.value)}
                    style={{ width: '100%', borderLeft: 'none' }}
                    placeholder="Monthly Commissions"
                    className="inputWithIcon"
                  />
                </div>
              </div>
            </FlexComponent>
          </FlexComponent>
        </div>
      )}

      {(showNewEntryForm || addMoreWorkHistory) && (
        <FlexComponent justifyContent="center" gap="1rem">
          <FlexComponent flexDirection="column" >
          <Button
            type="button"
            label="Save Work History"
            style={{ borderRadius: '5px', fontSize: '16px', padding: '10px', border: '1px solid #E0E4EC', background: Object.keys(validateWorkEntry(currentWork)).length === 0 ? "red" : '#0F0F0F', color: 'white' }}
            onClick={handleAddClick}
          />
          {Object.keys(validateWorkEntry(currentWork)).length === 0 && <div style={{fontSize: "16px", marginTop: ".5rem"}} >Clcik here to Save Work History</div>}
          </FlexComponent>
          {values.work_history.length > 0 && addMoreWorkHistory && (
            <Button
              type="button"
              label="Cancel"
              style={{ borderRadius: '5px', fontSize: '16px', padding: '5px 20px', border: '1px solid #E0E4EC', background: 'white', color: '#8C8C8C' }}
              onClick={() => {
                setAddMoreWorkHistory(false);
                setCurrentWork({
                  title: '',
                  yearly_income: '',
                  years_in_industry: '',
                  company_name: '',
                  employment_type: 'Salary - W2 Employee',
                  status: '',
                  start_date: null,
                  end_date: null,
                  full_address: '',
                  street_address: '',
                  city: '',
                  state: '',
                  zip_code: '',
                  industry_type: '',
                  monthly_overtime: '',
                  monthly_bonuses: '',
                  monthly_commissions: '',
                });
                setCurrentGapExplanation('');
                setError((prev) => ({ ...prev, newEntry: {} }));
              }}
            />
          )}
        </FlexComponent>
      )}

      {!showNewEntryForm && !addMoreWorkHistory && (
        <div style={{ textAlign: 'center', margin: '1rem' }}>
          <Button
            type="button"
            label="Add More Work History"
            style={{ borderRadius: '5px', fontSize: '16px', padding: '5px 20px', border: '1px solid #E0E4EC', background: 'white', color: '#8C8C8C' }}
            onClick={() => {
              setAddMoreWorkHistory(true);
            }}
          />
        </div>
      )}
      <div style={{ textAlign: 'center', marginTop: '1rem', width: '100%' }}>
        <ErrorMessage name="work_history" component="div" className="error" style={{ color: 'red' }} />
      </div>
      {gaps.length > 0 && (
        <FlexComponent className="inputFlexContainer" justifyContent="start" gap="1rem" alignItems="start" style={{ width: '100%', marginTop: '1rem' }}>
          <div className="inputContainerDiv" style={{ width: '100%' }}>
            <label style={{ fontSize: '20px', fontWeight: 'bold' }}>
              Okay great. It looks like there is a gap of about {formatDuration(gaps[gaps.length - 1].months)}. Can you tell me more about that?
            </label>
            <InputText
              value={currentGapExplanation}
              onChange={(e) => {
                setCurrentGapExplanation(e.target.value);
                setFieldValue('work_history_gap_explanation', e.target.value);
              }}
              style={{ width: '100%' }}
              placeholder="Explain gap in work history"
            />
          </div>
        </FlexComponent>
      )}
    </div>
  );
}

export default WorkHistoryComponent;